import React, { useCallback, useEffect, useRef, useState } from "react"
import { $isLinkNode } from "@lexical/link"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  getDOMSelection,
  SELECTION_CHANGE_COMMAND,
} from "lexical"
import { ExternalLink } from "lucide-react"
import { createPortal } from "react-dom"

import { OPEN_FLOATING_LINK_EDITOR_COMMAND } from "../floating-link-editor"
import mergeRegister, {
  getDOMRangeRect,
  getSelectedNode,
  setFloatingElemPosition,
} from "../floating-text-format-toolbar/utils"

export default function FloatingLinkPreviewPlugin({
  anchorElem,
}: {
  anchorElem: HTMLElement
}): React.JSX.Element | null {
  const [editor] = useLexicalComposerContext()
  const [linkUrl, setLinkUrl] = useState<string | null>(null)
  const floatingElemRef = useRef<HTMLDivElement | null>(null)

  const updateLinkPreview = useCallback(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection()
      let resolvedUrl: string | null = null

      if ($isRangeSelection(selection)) {
        const node = getSelectedNode(selection)
        const parent = node.getParent()
        const grandParent = parent?.getParent()

        if ($isLinkNode(node)) {
          resolvedUrl = node.getURL()
        } else if ($isLinkNode(parent)) {
          resolvedUrl = parent.getURL()
        } else if ($isLinkNode(grandParent)) {
          resolvedUrl = grandParent.getURL()
        }
      }

      setLinkUrl((current) => (current === resolvedUrl ? current : resolvedUrl))

      const elem = floatingElemRef.current
      if (!elem) return

      if (!resolvedUrl) {
        setFloatingElemPosition(null, elem, anchorElem)
        return
      }

      const nativeSelection = getDOMSelection(editor._window)
      const rootElement = editor.getRootElement()

      if (nativeSelection && rootElement && rootElement.contains(nativeSelection.anchorNode)) {
        const rangeRect = getDOMRangeRect(nativeSelection, rootElement)
        setFloatingElemPosition(rangeRect, elem, anchorElem, 8)
      }
    })
  }, [anchorElem, editor])

  useEffect(() => {
    const scrollerElem = anchorElem.parentElement
    const cleanup = mergeRegister(
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateLinkPreview()
          return false
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerUpdateListener(() => {
        updateLinkPreview()
      })
    )

    const handleReposition = () => {
      const elem = floatingElemRef.current
      if (!elem || !linkUrl) return

      editor.getEditorState().read(() => {
        const nativeSelection = getDOMSelection(editor._window)
        const rootElement = editor.getRootElement()

        if (nativeSelection && rootElement && rootElement.contains(nativeSelection.anchorNode)) {
          const rangeRect = getDOMRangeRect(nativeSelection, rootElement)
          setFloatingElemPosition(rangeRect, elem, anchorElem, 8)
        }
      })
    }

    window.addEventListener("resize", handleReposition)
    scrollerElem?.addEventListener("scroll", handleReposition)

    // run once on mount to sync with initial selection
    updateLinkPreview()
    handleReposition()

    return () => {
      cleanup()
      window.removeEventListener("resize", handleReposition)
      scrollerElem?.removeEventListener("scroll", handleReposition)
    }
  }, [anchorElem, editor, linkUrl, updateLinkPreview])

  useEffect(() => {
    if (!linkUrl) return
    const elem = floatingElemRef.current
    if (!elem) return

    editor.getEditorState().read(() => {
      const nativeSelection = getDOMSelection(editor._window)
      const rootElement = editor.getRootElement()

      if (nativeSelection && rootElement && rootElement.contains(nativeSelection.anchorNode)) {
        const rangeRect = getDOMRangeRect(nativeSelection, rootElement)
        setFloatingElemPosition(rangeRect, elem, anchorElem, 8)
      }
    })
  }, [anchorElem, editor, linkUrl])

  const handleOpenLink = useCallback(() => {
    if (linkUrl) {
      window.open(linkUrl, "_blank", "noopener,noreferrer")
    }
  }, [linkUrl])

  const handleEditLink = useCallback(() => {
    editor.dispatchCommand(OPEN_FLOATING_LINK_EDITOR_COMMAND, undefined)
  }, [editor])

  if (!linkUrl) {
    return null
  }

  return createPortal(
    <div
      ref={floatingElemRef}
      className="floating-link-preview bg-background absolute top-0 left-0 z-50 flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm shadow-lg"
    >
      <button
        type="button"
        onClick={handleOpenLink}
        className="text-muted-foreground hover:text-foreground h-3 w-3 p-0"
        title="Open link in new tab"
        aria-label="Open link in new tab"
      >
        <ExternalLink className="h-3 w-3" />
      </button>
      <button
        type="button"
        onClick={handleEditLink}
        className="text-muted-foreground hover:text-foreground max-w-xs truncate text-left"
        title="Click to edit link"
        aria-label="Click to edit link"
      >
        {linkUrl}
      </button>
    </div>,
    anchorElem
  )
}
